import { useState } from "react";
import { Pencil, Plus, Search, SlidersHorizontal, Trash2 } from "lucide-react";
import { ApiError } from "@/lib/api";
import { Badge, Button, Input, Select } from "@/components/ui/primitives";
import { ConfirmButton } from "@/components/ui/ConfirmButton";
import { RequirePermission } from "@/features/auth/RequirePermission";
import { VendorFormDialog } from "./VendorFormDialog";
import { ProductFormDialog } from "./ProductFormDialog";
import { AdjustStockDialog } from "./AdjustStockDialog";
import { PurchaseOrderDialog } from "./PurchaseOrderDialog";
import { RmaDialog } from "./RmaDialog";
import {
  useDeleteProduct,
  useDeleteVendor,
  useProducts,
  usePurchaseOrders,
  useRmas,
  useVendors,
} from "./hooks";
import {
  PO_STATUS_LABELS,
  RMA_STATUS_LABELS,
  poTone,
  rmaTone,
} from "./labels";
import {
  getPo,
  getProduct,
  getRma,
  type Product,
  type ProductDetail,
  type PurchaseOrderDetail,
  type RmaDetail,
  type Vendor,
  type VendorDetail,
} from "./api";

const PAGE_SIZE = 10;
type Tab = "products" | "vendors" | "orders" | "rmas";

const TABS: { id: Tab; label: string }[] = [
  { id: "products", label: "Products" },
  { id: "vendors", label: "Vendors" },
  { id: "orders", label: "Purchase orders" },
  { id: "rmas", label: "RMAs" },
];

export default function InventoryPage() {
  const [tab, setTab] = useState<Tab>("products");
  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Inventory</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Stock, suppliers, purchase orders and returns.
        </p>
      </div>

      <div className="flex gap-1 border-b border-border" role="tablist" aria-label="Inventory views">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            className={
              "border-b-2 px-4 py-2 text-sm font-medium " +
              (tab === t.id
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground")
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "products" && <ProductsTab />}
      {tab === "vendors" && <VendorsTab />}
      {tab === "orders" && <OrdersTab />}
      {tab === "rmas" && <RmasTab />}
    </div>
  );
}

function Pager({
  total,
  noun,
  page,
  setPage,
}: {
  total: number;
  noun: string;
  page: number;
  setPage: (fn: (p: number) => number) => void;
}) {
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  return (
    <div className="flex items-center justify-between text-sm text-muted-foreground">
      <span>{`${total} ${noun}${total === 1 ? "" : "s"}`}</span>
      <div className="flex items-center gap-2">
        <Button variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
          Previous
        </Button>
        <span>
          Page {page} of {totalPages}
        </span>
        <Button variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
          Next
        </Button>
      </div>
    </div>
  );
}

function ErrorRow({ error }: { error: string | null }) {
  if (!error) return null;
  return (
    <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
      {error}
    </div>
  );
}

// ------------------------------- products -------------------------------

function ProductsTab() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [lowOnly, setLowOnly] = useState(false);
  const { data } = useProducts({
    page,
    page_size: PAGE_SIZE,
    search: search.trim(),
    ...(lowOnly ? { low_stock: true } : {}),
  });
  const remove = useDeleteProduct();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ProductDetail | null>(null);
  const [adjusting, setAdjusting] = useState<Product | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong.");
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <div className="relative min-w-[200px] flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" aria-hidden="true" />
            <Input
              aria-label="Search products"
              placeholder="Name, SKU, barcode"
              className="pl-8"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <label className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
            <input type="checkbox" checked={lowOnly} onChange={(e) => { setLowOnly(e.target.checked); setPage(1); }} />
            Low stock only
          </label>
        </div>
        <RequirePermission permission="inventory:manage">
          <Button onClick={() => { setEditing(null); setFormOpen(true); }}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            Add product
          </Button>
        </RequirePermission>
      </div>

      <ErrorRow error={error} />

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-4 py-2.5 font-medium">Product</th>
              <th className="px-4 py-2.5 font-medium">On hand</th>
              <th className="px-4 py-2.5 font-medium">Reorder</th>
              <th className="px-4 py-2.5 font-medium">Cost / Price</th>
              <th className="px-4 py-2.5 font-medium">Vendor</th>
              <th className="px-4 py-2.5 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data?.items.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                  No products match these filters.
                </td>
              </tr>
            )}
            {data?.items.map((p) => (
              <tr key={p.id} className="border-t border-border">
                <td className="px-4 py-3">
                  <div className="font-medium">{p.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {p.sku}
                    {p.barcode ? ` · ${p.barcode}` : ""}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="tabular-nums">{p.quantity_on_hand}</span>{" "}
                  {p.low_stock && <Badge tone="warning">Low</Badge>}
                </td>
                <td className="px-4 py-3 tabular-nums text-muted-foreground">{p.reorder_level}</td>
                <td className="px-4 py-3 text-muted-foreground">
                  {p.unit_cost ? `₹${p.unit_cost}` : "—"} / {p.sale_price ? `₹${p.sale_price}` : "—"}
                </td>
                <td className="px-4 py-3 text-muted-foreground">{p.preferred_vendor_name ?? "—"}</td>
                <td className="px-4 py-3">
                  <RequirePermission permission="inventory:manage">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" aria-label={`Adjust stock for ${p.name}`} onClick={() => setAdjusting(p)}>
                        <SlidersHorizontal className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        aria-label={`Edit ${p.name}`}
                        onClick={() =>
                          void run(async () => {
                            setEditing(await getProduct(p.id));
                            setFormOpen(true);
                          })
                        }
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <ConfirmButton aria-label={`Remove ${p.name}`} title="Delete product?" message={<>Remove <span className="font-medium text-foreground">{p.name}</span>?</>} onConfirm={() => run(() => remove.mutateAsync(p.id))}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </ConfirmButton>
                    </div>
                  </RequirePermission>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pager total={data?.total ?? 0} noun="product" page={page} setPage={setPage} />

      <ProductFormDialog open={formOpen} onClose={() => setFormOpen(false)} editing={editing} />
      <AdjustStockDialog product={adjusting} onClose={() => setAdjusting(null)} />
    </div>
  );
}

// ------------------------------- vendors --------------------------------

function VendorsTab() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const { data } = useVendors({ page, page_size: PAGE_SIZE, search: search.trim() });
  const remove = useDeleteVendor();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Vendor | VendorDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong.");
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative min-w-[200px] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <Input
            aria-label="Search vendors"
            placeholder="Name, code, contact"
            className="pl-8"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
        <RequirePermission permission="inventory:manage">
          <Button onClick={() => { setEditing(null); setFormOpen(true); }}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            Add vendor
          </Button>
        </RequirePermission>
      </div>

      <ErrorRow error={error} />

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-4 py-2.5 font-medium">Vendor</th>
              <th className="px-4 py-2.5 font-medium">Contact</th>
              <th className="px-4 py-2.5 font-medium">Phone</th>
              <th className="px-4 py-2.5 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data?.items.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">
                  No vendors yet.
                </td>
              </tr>
            )}
            {data?.items.map((v) => (
              <tr key={v.id} className="border-t border-border">
                <td className="px-4 py-3">
                  <div className="font-medium">{v.name}</div>
                  <div className="text-xs text-muted-foreground">{v.code}</div>
                </td>
                <td className="px-4 py-3 text-muted-foreground">{v.contact_name ?? "—"}</td>
                <td className="px-4 py-3 text-muted-foreground">{v.phone ?? "—"}</td>
                <td className="px-4 py-3">
                  <RequirePermission permission="inventory:manage">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" aria-label={`Edit ${v.name}`} onClick={() => { setEditing(v); setFormOpen(true); }}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <ConfirmButton aria-label={`Remove ${v.name}`} title="Delete vendor?" message={<>Remove vendor <span className="font-medium text-foreground">{v.name}</span>?</>} onConfirm={() => run(() => remove.mutateAsync(v.id))}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </ConfirmButton>
                    </div>
                  </RequirePermission>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pager total={data?.total ?? 0} noun="vendor" page={page} setPage={setPage} />
      <VendorFormDialog open={formOpen} onClose={() => setFormOpen(false)} editing={editing} />
    </div>
  );
}

// ---------------------------- purchase orders ---------------------------

function OrdersTab() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const { data } = usePurchaseOrders({
    page,
    page_size: PAGE_SIZE,
    status: statusFilter,
  });

  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState<PurchaseOrderDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function open(id: string) {
    setError(null);
    try {
      setViewing(await getPo(id));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load the PO.");
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Select
          aria-label="Filter by status"
          className="w-auto min-w-[160px]"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
        >
          <option value="">All statuses</option>
          {Object.entries(PO_STATUS_LABELS).map(([v, label]) => (
            <option key={v} value={v}>{label}</option>
          ))}
        </Select>
        <RequirePermission permission="inventory:manage">
          <Button onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            New purchase order
          </Button>
        </RequirePermission>
      </div>

      <ErrorRow error={error} />

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-4 py-2.5 font-medium">PO</th>
              <th className="px-4 py-2.5 font-medium">Vendor</th>
              <th className="px-4 py-2.5 font-medium">Status</th>
              <th className="px-4 py-2.5 font-medium">Lines</th>
              <th className="px-4 py-2.5 font-medium">Total</th>
            </tr>
          </thead>
          <tbody>
            {data?.items.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                  No purchase orders yet.
                </td>
              </tr>
            )}
            {data?.items.map((po) => (
              <tr
                key={po.id}
                className="cursor-pointer border-t border-border hover:bg-accent/50"
                onClick={() => void open(po.id)}
              >
                <td className="px-4 py-3 font-medium">{po.po_number}</td>
                <td className="px-4 py-3 text-muted-foreground">{po.vendor_name}</td>
                <td className="px-4 py-3">
                  <Badge tone={poTone(po.status)}>{PO_STATUS_LABELS[po.status]}</Badge>
                </td>
                <td className="px-4 py-3 tabular-nums">{po.line_count}</td>
                <td className="px-4 py-3 tabular-nums">₹{po.total_cost}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pager total={data?.total ?? 0} noun="order" page={page} setPage={setPage} />

      <PurchaseOrderDialog open={creating} onClose={() => setCreating(false)} existing={null} />
      <PurchaseOrderDialog open={viewing !== null} onClose={() => setViewing(null)} existing={viewing} />
    </div>
  );
}

// -------------------------------- RMAs ----------------------------------

function RmasTab() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const { data } = useRmas({ page, page_size: PAGE_SIZE, status: statusFilter });

  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState<RmaDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function open(id: string) {
    setError(null);
    try {
      setViewing(await getRma(id));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load the RMA.");
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Select
          aria-label="Filter by status"
          className="w-auto min-w-[160px]"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
        >
          <option value="">All statuses</option>
          {Object.entries(RMA_STATUS_LABELS).map(([v, label]) => (
            <option key={v} value={v}>{label}</option>
          ))}
        </Select>
        <RequirePermission permission="inventory:manage">
          <Button onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            Raise RMA
          </Button>
        </RequirePermission>
      </div>

      <ErrorRow error={error} />

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-4 py-2.5 font-medium">RMA</th>
              <th className="px-4 py-2.5 font-medium">Product</th>
              <th className="px-4 py-2.5 font-medium">Vendor</th>
              <th className="px-4 py-2.5 font-medium">Qty</th>
              <th className="px-4 py-2.5 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {data?.items.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                  No RMAs yet.
                </td>
              </tr>
            )}
            {data?.items.map((r) => (
              <tr
                key={r.id}
                className="cursor-pointer border-t border-border hover:bg-accent/50"
                onClick={() => void open(r.id)}
              >
                <td className="px-4 py-3 font-medium">{r.rma_number}</td>
                <td className="px-4 py-3 text-muted-foreground">{r.product_name}</td>
                <td className="px-4 py-3 text-muted-foreground">{r.vendor_name ?? "—"}</td>
                <td className="px-4 py-3 tabular-nums">{r.quantity}</td>
                <td className="px-4 py-3">
                  <Badge tone={rmaTone(r.status)}>{RMA_STATUS_LABELS[r.status]}</Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pager total={data?.total ?? 0} noun="RMA" page={page} setPage={setPage} />

      <RmaDialog open={creating} onClose={() => setCreating(false)} existing={null} />
      <RmaDialog open={viewing !== null} onClose={() => setViewing(null)} existing={viewing} />
    </div>
  );
}
